import { isEqual } from 'lodash';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { ColumnDensity, SORT_ORDER } from '@common/types';

export type PersistedOperatorColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialOperatorColumns: PersistedOperatorColumn[] = [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'partner_name',
    headerName: 'Partner',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'city',
    headerName: 'City / State',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'county',
    headerName: 'County',
    minWidth: 140,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'building_count',
    headerName: 'Buildings',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
  },
];

export const initialOperatorPinnedColumns: GridPinnedColumnFields = {
  left: [],
  right: [],
};

export type ColumnNames = 'name' | 'partner_name' | 'city' | 'building_count';

export interface IOperatorTableFilters {
  search: string;
  sort_by: ColumnNames;
  sort_order: SORT_ORDER;
  page: number;
  limit: number;
  county: string;
  state: string;
  partner_id: string;
}

const getDefaultFilters = (): IOperatorTableFilters => ({
  search: '',
  sort_order: 'asc',
  sort_by: 'name',
  page: 1,
  limit: 10,
  county: '',
  state: '',
  partner_id: '',
});

type TKeyOfFilters = keyof IOperatorTableFilters;

const getAppliedFiltersCount = (
  initialFiltersList: IOperatorTableFilters,
  appliedFilters: IOperatorTableFilters,
): number => {
  const filtersKeyList: TKeyOfFilters[] = Object.keys(appliedFilters).filter(
    key =>
      key !== 'page' &&
      key !== 'limit' &&
      key !== 'search' &&
      key !== 'sort_order' &&
      key !== 'sort_by',
  ) as TKeyOfFilters[];

  return filtersKeyList.reduce(
    (count, key) =>
      isEqual(initialFiltersList[key], appliedFilters[key]) ? count : count + 1,
    0,
  );
};

type TOperatorTableStore = {
  filters: IOperatorTableFilters;
  setFilters: (newFilters: Partial<IOperatorTableFilters>) => void;
  clearStore: () => void;
  appliedFiltersCount: number;

  columns: PersistedOperatorColumn[];
  setColumns: (columns: PersistedOperatorColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  setDensity: (density: ColumnDensity) => void;
  resetView: () => void;
};

export const useOperatorTableStore = create<TOperatorTableStore>()(
  persist(
    set => ({
      filters: getDefaultFilters(),
      setFilters: newFilters =>
        set(state => {
          const updatedFilters = { ...state.filters, ...newFilters };
          return {
            filters: updatedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedFilters,
            ),
          };
        }),
      clearStore: () =>
        set(() => ({
          filters: getDefaultFilters(),
          appliedFiltersCount: 0,
        })),
      appliedFiltersCount: 0,

      columns: initialOperatorColumns,
      setColumns: columns => set({ columns }),
      pinnedColumns: initialOperatorPinnedColumns,
      setPinnedColumns: pinnedColumns => set({ pinnedColumns }),
      hiddenColumns: [],
      setHiddenColumns: hiddenColumns => set({ hiddenColumns }),
      density: 'comfortable',
      setDensity: density => set({ density }),
      resetView: () =>
        set({
          columns: initialOperatorColumns,
          pinnedColumns: initialOperatorPinnedColumns,
          hiddenColumns: [],
          density: 'comfortable',
        }),
    }),
    {
      name: 'operator-table-store',
      partialize: state => ({
        columns: state.columns,
        pinnedColumns: state.pinnedColumns,
        hiddenColumns: state.hiddenColumns,
        density: state.density,
        sort_by: state.filters.sort_by,
        sort_order: state.filters.sort_order,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<
          TOperatorTableStore &
            Pick<IOperatorTableFilters, 'sort_by' | 'sort_order'>
        >;

        const mergedFilters: IOperatorTableFilters = {
          ...currentState.filters,
          ...(persisted.sort_by && { sort_by: persisted.sort_by }),
          ...(persisted.sort_order && { sort_order: persisted.sort_order }),
        };

        return {
          ...currentState,
          ...persisted,
          filters: mergedFilters,
        };
      },
    },
  ),
);
